import { Instagram, Youtube, Facebook, Mail } from "lucide-react";
import Logo, { WhatsAppIcon } from "@/components/Logo";
import FolkBorder from "@/components/FolkBorder";
import { scrollToId } from "@/lib/api";

const WaveTop = () => (
  <svg viewBox="0 0 1440 90" className="block w-full text-navy" preserveAspectRatio="none" aria-hidden="true">
    <path
      fill="currentColor"
      d="M0,40 C120,80 240,10 360,35 C480,60 600,85 720,60 C840,35 960,10 1080,35 C1200,60 1320,80 1440,45 L1440,90 L0,90 Z"
    />
    <path
      fill="#2e7f86"
      opacity="0.55"
      d="M0,60 C140,85 280,35 420,50 C560,65 700,88 840,70 C980,52 1120,30 1260,50 C1350,62 1400,70 1440,66 L1440,90 L0,90 Z"
    />
  </svg>
);

const Footer = ({ content }) => {
  const links = [
    { label: "About", href: "#about" },
    { label: "Vision", href: "#vision" },
    { label: "Gallery", href: "#gallery" },
    { label: "Events", href: "/events" },
    { label: "Reels", href: "#reels" },
    { label: "Blog", href: "#blog" },
    { label: "Contact", href: "#contact" },
  ];

  const socials = [
    { icon: Instagram, url: content.instagram_url, label: "Instagram" },
    { icon: Youtube, url: content.youtube_url, label: "YouTube" },
    { icon: WhatsAppIcon, url: content.whatsapp_url, label: "WhatsApp" },
    { icon: Facebook, url: content.facebook_url, label: "Facebook" },
    { icon: Mail, url: `mailto:${content.contact_email}`, label: "Email" },
  ].filter((s) => s.url);

  return (
    <footer className="bg-navy" data-testid="footer">
      <WaveTop />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-14 grid md:grid-cols-3 gap-10 items-start">
        <div>
          <Logo light />
          <p className="mt-4 font-body text-paper/70 text-sm leading-relaxed" data-testid="footer-blurb">
            {content.footer_blurb || "A kiting team from Odisha, united by a shared passion for kites, wind and the freedom of the open sky."}
          </p>
          {socials.length ? (
            <div className="mt-5 flex gap-3" data-testid="footer-socials">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target={s.url.startsWith("mailto") ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full border-2 border-paper/30 grid place-items-center text-paper hover:border-marigold hover:text-marigold transition-colors duration-300"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          ) : null}
        </div>

        <div className="md:justify-self-center">
          <p className="font-display font-bold text-marigold tracking-widest text-sm uppercase" data-testid="footer-links-title">
            {content.footer_links_title || "Chart The Wind"}
          </p>
          <ul className="mt-4 space-y-2.5">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={l.href.startsWith("/") ? undefined : (e) => {
                    e.preventDefault();
                    scrollToId(l.href);
                  }}
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  className="font-body text-paper/80 hover:text-marigold transition-colors duration-300"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:justify-self-end md:text-right">
          <p className="font-script text-3xl text-paper leading-snug" data-testid="footer-script">
            {content.footer_script || "More Wind"}
            <br />
            {content.footer_script_2 || "More Smiles"}
          </p>
          <p className="mt-3 font-body text-paper/60 text-sm" data-testid="footer-email">
            {content.contact_email}
          </p>
        </div>
      </div>
      <div className="pb-6 text-center font-body text-paper/50 text-xs tracking-wide" data-testid="footer-copy">
        © {new Date().getFullYear()} Wind Skaters — Team Odisha · Fly Higher. Together
      </div>
      <FolkBorder />
    </footer>
  );
};

export default Footer;
